import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useContext } from 'react';
import { CALENDAR_ON_REDEMPTION_PRODUCT } from '../../../../brands/default/content/components/deal';
import { getSelectValues } from '../../../../helpers/checkout';
import { formatCurrency } from '../../../../helpers/currency';
import ThemeContext from '../../../../providers/ThemeProvider';
import PrimaryButton from '../../../_generic/button/PrimaryButton';
import CustomNativeSelect from '../../../_generic/custom-native-select/CustomNativeSelect';

const CalendarOnRedemptionProduct = ({
  currency,
  product,
  quantity,
  selected,
  onSelect,
  onQuantityChange,
}) => {
  const theme = useContext(ThemeContext);
  const { now, save, was } = CALENDAR_ON_REDEMPTION_PRODUCT;

  /** 1 - max purchasable capped at 30 */
  const options = getSelectValues(
    1,
    Math.min(product.purchasableQuantity, product.quantityAvailable, 30),
  );

  const onClick = () => {
    onSelect(product.dealVoucherProductId);
  };

  const onKeyDown = (event) => {
    if (event.key === 'Enter') {
      onSelect(product.dealVoucherProductId);
    }
  };

  return (
    <>
      <div
        aria-label={`Select ${product.friendlyName}`}
        className={classNames('booking-calendar__product', {
          'booking-calendar__product--selected': selected,
        })}
        onClick={onClick}
        onKeyDown={onKeyDown}
        role="button"
        tabIndex={0}
      >
        <div className="booking-calendar__product-desc">
          <h3 className="booking-calendar__product-title">
            {product.friendlyName}
          </h3>
        </div>
        <div className="booking-calendar__product-desc">
          <PrimaryButton
            className={classNames('size-smaller inline', {
              'outline thin-outline': selected,
            })}
            tag="span"
          >
            {selected ? 'Chosen' : 'Choose'}
          </PrimaryButton>
          <div className="booking-calendar__product-prices">
            {product.discountRepresentation === 'PERCENTAGE' && (
              <>
                <span className="booking-calendar__product-label">
                  {was}&nbsp;
                </span>
                <span className="booking-calendar__product-was-strike booking-calendar__product-value">
                  <span>
                    {formatCurrency(
                      currency,
                      Number.parseFloat(product.originalPrice),
                      false,
                      true,
                    )}
                  </span>
                </span>
                &nbsp;
                <span className="booking-calendar__product-label">
                  {save}&nbsp;
                </span>
                <span className="booking-calendar__product-value">
                  {product.discount}%
                </span>
                &nbsp;
              </>
            )}
            <span className="booking-calendar__product-label">{now}&nbsp;</span>
            <span className="booking-calendar__product-price booking-calendar__product-value">
              {formatCurrency(
                currency,
                Number.parseFloat(product.price),
                false,
                true,
              )}
            </span>
          </div>
        </div>
        <div className="booking-calendar__product-desc">
          {selected && product.showQuantity && (
            <label className="booking-calendar__quantity">
              Quantity:
              <CustomNativeSelect
                className="bg-white"
                onChange={(event) =>
                  onQuantityChange(Number.parseInt(event.target.value, 10))
                }
                value={quantity}
              >
                {options.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </CustomNativeSelect>
            </label>
          )}
        </div>
      </div>
      <style jsx>{`
        .booking-calendar__product {
          position: relative;
          padding: 10px;
          border-radius: 4px;
          border: 2px solid transparent;
          line-height: 24px;
          margin-bottom: 8px;
          background-color: ${theme.colors.navbackground};
          outline: none;
        }
        .booking-calendar__product:last-child {
          margin-bottom: 0;
        }

        .booking-calendar__product:focus,
        .booking-calendar__product:hover {
          border: 2px solid #cccccc;
        }
        .booking-calendar__product--selected {
          background-color: ${theme.commoncolors.greyx3lighter};
          border: 2px solid ${theme.colors.primary} !important;
        }
        .booking-calendar__product--selected .booking-calendar__product-title {
          color: ${theme.colors.primary};
        }
        .booking-calendar__product-title {
          color: ${theme.colors.navlink};
          display: inline;
          margin-left: 5px;
          margin-right: 5px;
        }
        .booking-calendar__product-was-strike span {
          position: relative;
        }
        .booking-calendar__product-was-strike span:before {
          border-top: 1px solid #000;
          content: '';
          left: 0;
          margin-left: -15%;
          position: absolute;
          right: 0;
          top: 50%;
          transform: rotate(-11deg);
          width: 130%;
        }
        .booking-calendar__product-desc {
          display: flex;
          justify-content: space-between;
          margin-bottom: 2px;
        }
        .booking-calendar__product-prices {
          align-self: flex-end;
          padding: 3px 11px;
          background-color: white;
          border: 1px solid #cccccc;
          border-radius: 60px;
          line-height: 21px;
          margin-left: auto;
        }
        .booking-calendar__product-label {
          text-transform: capitalize;
          font-weight: bold;
          margin: 0;
          line-height: 1em;
          font-size: 12px;
        }
        .booking-calendar__product-value {
          font-size: 17px;
          margin: 0;
        }
        .booking-calendar__product-price {
          color: ${theme.colors.primary};
        }
        .booking-calendar__quantity {
          align-self: flex-end;
          font-weight: bold;
          margin-bottom: 0;
          margin-top: 4px;
          margin-left: auto;
        }
        :global(.booking-calendar__quantity .custom-select) {
          margin-left: 8px;
        }
      `}</style>
    </>
  );
};

CalendarOnRedemptionProduct.propTypes = {
  currency: PropTypes.string,
  onQuantityChange: PropTypes.func,
  onSelect: PropTypes.func,
  product: PropTypes.object,
  quantity: PropTypes.number,
  selected: PropTypes.bool,
};

CalendarOnRedemptionProduct.defaultProps = {
  currency: 'gbp',
  onQuantityChange: () => false,
  onSelect: () => false,
  product: {},
  quantity: 1,
  selected: false,
};

export default CalendarOnRedemptionProduct;
